import type { Notification, NotificationActor } from "../interfaces/notifications.interface";

/** Resolve actor from notification (actor or _actor). */
export function getActorData(notif: Notification): NotificationActor | null {
  return notif.actor || notif._actor || null;
}

function parseMeta(notif: any): Record<string, unknown> | null {
  if (!notif) return null;
  // Check common locations for metadata
  const meta = notif.meta || notif.metadata || notif.payload?.meta || notif.data?.meta || notif.payload || notif.data;
  
  if (meta == null) return null;
  if (typeof meta === "object" && !Array.isArray(meta)) return meta as Record<string, unknown>;
  if (typeof meta === "string") {
    try {
      return JSON.parse(meta) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return null;
}

function tryUrl(u?: string | null): string | null {
  if (!u) return null;
  if (!/^https?:\/\//i.test(u) && process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL.replace(/\/$/, "")}/${u.replace(/^\//, "")}`;
  }
  return u;
}

/**
 * Get display name for the notification actor (sender, seller, etc.).
 * Used consistently across Notifications page, Header dropdown, Messages, NotificationDetail.
 */
export function getActorName(notif: Notification): string {
  const meta = parseMeta(notif);

  if (notif.type === "product") {
    if (notif.title?.includes("Your listing is available")) return notif.title;
    const sellerName =
      (meta?.sellerName as string) ?? (notif.meta as any)?.sellerName ?? notif.title;
    if (sellerName && sellerName !== "A seller" && sellerName !== "Notification" && sellerName !== "Someone") {
      return sellerName;
    }
    const actor = getActorData(notif);
    if (actor?.firstName || actor?.lastName) {
      return `${actor.firstName ?? ""} ${actor.lastName ?? ""}`.trim() || "A seller";
    }
    return "A seller";
  }

  if (notif.type === "NEW_MESSAGE" || notif.type === "message" || notif.type === "MESSAGE_REACTION") {
    const actor = getActorData(notif);
    if (actor?.firstName || actor?.lastName) {
      return `${actor.firstName ?? ""} ${actor.lastName ?? ""}`.trim() || "Someone";
    }
    if (notif.title && notif.title !== "Notification" && notif.title !== "Someone") {
      return notif.title;
    }
    if (notif.body?.includes(":")) {
      const namePart = notif.body.split(":")[0];
      if (namePart.length < 50) return namePart;
    }
    return "Someone";
  }

  return "Someone";
}

/**
 * Get profile image URL for the notification actor, or null for logo/fallback.
 * Caller should use default avatar for message type when null.
 */
export function getActorImage(notif: Notification): string | null {
  const meta = parseMeta(notif);
  const actor = getActorData(notif);

  if (notif.type === "message" || notif.type === "NEW_MESSAGE") {
    const candidates = [
      actor?.profileImage,
      actor?.avatar,
      notif.senderAvatar,
      notif.senderImage,
      (meta?.senderImage as string) ?? (notif.meta as any)?.senderImage,
      (meta?.actorImage as string) ?? (notif.meta as any)?.actorImage,
    ];
    for (const c of candidates) {
      const resolved = tryUrl(c);
      if (resolved) return resolved;
    }
    return null; // Caller uses default avatar for messages
  }

  if (notif.type === "product") {
    const sellerImage =
      (meta?.sellerImage as string) ??
      (notif.meta as any)?.sellerImage ??
      actor?.profileImage ??
      notif.sellerImage;
    const resolved = tryUrl(sellerImage);
    return resolved;
  }

  const candidates = [
    actor?.profileImage,
    actor?.avatar,
    notif.senderAvatar,
    (meta?.senderImage as string) ?? (notif.meta as any)?.senderImage,
    (meta?.actorImage as string) ?? (notif.meta as any)?.actorImage,
  ];
  for (const c of candidates) {
    const resolved = tryUrl(c);
    if (resolved) return resolved;
  }
  return null;
}

/**
 * Robustly extract conversationId from notification meta or body.
 */
export function getConversationIdFromNotification(notif: any): string | null {
  if (!notif) return null;

  const meta = parseMeta(notif);

  const possibleId =
    meta?.conversationId ||
    meta?.convoId ||
    meta?.chat_id ||
    meta?.chatId ||
    meta?.id ||
    notif.conversationId ||
    notif.conversation_id ||
    notif.convoId ||
    notif.chatId ||
    notif.chat_id ||
    notif.payload?.conversationId ||
    notif.data?.conversationId;

  return possibleId ? String(possibleId) : null;
}

/**
 * Robustly extract actor/participant ID from notification.
 * Checks top-level actorId, senderId, nested actor.id, or meta fields.
 */
export function getActorIdFromNotification(notif: any): string | null {
  if (!notif) return null;

  const meta = parseMeta(notif);
  const actor = getActorData(notif);

  // Broad set of possible participant ID keys
  const possibleId =
    notif.actorId ||
    notif.senderId ||
    notif.userId ||
    notif.participantId ||
    notif.otherParticipantId ||
    actor?.id ||
    (actor as any)?.userId ||
    notif.sender?.id ||
    notif.payload?.actorId ||
    notif.payload?.senderId ||
    notif.data?.actorId ||
    meta?.actorId ||
    meta?.senderId ||
    meta?.sender_id ||
    meta?.participantId ||
    meta?.otherParticipantId ||
    meta?.userId ||
    meta?.buyerId ||
    meta?.sellerId ||
    null;

  return possibleId ? String(possibleId) : null;
}

/**
 * Check if a notification is related to messaging (direct message or reaction).
 */
export function isMessageOrReactionNotification(notif: any): boolean {
  if (!notif) return false;
  
  const type = notif.type;
  const meta = parseMeta(notif.meta);

  return (
    type === 'message' ||
    type === 'NEW_MESSAGE' ||
    type === 'MESSAGE_REACTION' ||
    meta?.messageType === 'REACTION' ||
    meta?.type === 'reaction' ||
    meta?.type === 'message' ||
    !!meta?.conversationId
  );
}
